import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  FaPlus, FaBox, FaKey, FaSearch, FaEdit, FaTrash, FaEye,
  FaCrown, FaArrowUp, FaCheck, FaSpinner, FaImage, FaComments,
  FaPause, FaPlay
} from 'react-icons/fa';

export default function MyAnnonce() {
  const { i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [existingAd, setExistingAd] = useState(null);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [conversations, setConversations] = useState([]);
  
  const [adType, setAdType] = useState('product');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: '',
    location: '',
    price: '',
    images: [],
    contact_phone: '',
    contact_email: user?.email || '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const subRes = await axios.get(`${API}/subscriptions/user/${user.id}`);
      setSubscription(subRes.data);
      
      const catRes = await axios.get(`${API}/categories`);
      setCategories(catRes.data);
      
      await checkExistingAd();
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkExistingAd = async () => {
    try {
      // Check products created by user
      const productsRes = await axios.get(`${API}/products?include_status=all`);
      const userProduct = productsRes.data.find(p => p.created_by_user_id === user.id);
      
      if (userProduct) {
        setExistingAd({ type: 'product', data: userProduct });
        // Fetch conversations for this product
        await fetchConversations(userProduct.id, 'product');
        return;
      }
      
      // Check requests
      const requestsRes = await axios.get(`${API}/requests/user/${user.id}`);
      if (requestsRes.data.length > 0) {
        setExistingAd({ type: 'request', data: requestsRes.data[0] });
        await fetchConversations(requestsRes.data[0].id, 'request');
        return;
      }
      
      setExistingAd(null);
      setConversations([]);
    } catch (error) {
      console.error('Error checking existing ads:', error);
    }
  };

  const fetchConversations = async (adId, adType) => {
    try {
      if (adType === 'request') {
        const res = await axios.get(`${API}/requests/conversations/by-request/${adId}?user_id=${user.id}`);
        setConversations(res.data || []);
      } else if (adType === 'product') {
        // Check if there are quote requests for this product
        const res = await axios.get(`${API}/quotes/by-product/${adId}?user_id=${user.id}`);
        setConversations(res.data || []);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setConversations([]);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingImage(true);
    const uploadedUrls = [];
    
    for (const file of files.slice(0, 5)) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await axios.post(`${API}/upload-image`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        const imageUrl = response.data.url || response.data.image_url;
        if (imageUrl) uploadedUrls.push(imageUrl);
      } catch (error) {
        console.error('Error uploading image:', error);
      }
    }
    
    if (uploadedUrls.length > 0) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls].slice(0, 5)
      }));
      toast.success(`${uploadedUrls.length} image(s) ajoutée(s)`);
    }
    
    setUploadingImage(false);
    e.target.value = '';
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const openEditDialog = () => {
    if (!existingAd) return;
    
    const ad = existingAd.data;
    setEditMode(true);
    setAdType(existingAd.type);
    setFormData({
      title: ad.name_fr || ad.title_fr || '',
      description: ad.description_fr || '',
      category_id: ad.category_id || '',
      location: ad.location || '',
      price: ad.price_new || ad.price_rental || ad.budget || '',
      images: ad.images || [],
      contact_phone: '',
      contact_email: user?.email || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Veuillez entrer un titre');
      return;
    }
    
    setSubmitting(true);
    
    try {
      if (editMode && existingAd) {
        // Update existing ad
        if (existingAd.type === 'product') {
          const payload = {
            name_fr: formData.title,
            name_en: formData.title,
            description_fr: formData.description,
            description_en: formData.description,
            category_id: formData.category_id || null,
            location: formData.location,
            price_new: adType === 'product' && formData.price ? parseFloat(formData.price) : null,
            price_rental: adType === 'rental' && formData.price ? parseFloat(formData.price) : null,
            show_price: true,
            images: formData.images,
          };
          await axios.put(`${API}/products/${existingAd.data.id}`, payload);
        } else if (existingAd.type === 'request') {
          const payload = {
            title_fr: formData.title,
            title_en: formData.title,
            description_fr: formData.description,
            description_en: formData.description,
            category_id: formData.category_id || null,
            location: formData.location,
            budget: formData.price ? parseFloat(formData.price) : null,
            images: formData.images,
          };
          await axios.put(`${API}/requests/update/${existingAd.data.id}?user_id=${user.id}`, payload);
        }
        
        toast.success('Annonce modifiée avec succès !');
      } else {
        // Create new ad
        if (adType === 'product' || adType === 'rental') {
          const payload = {
            name_fr: formData.title,
            name_en: formData.title,
            description_fr: formData.description,
            description_en: formData.description,
            category_id: formData.category_id || null,
            location: formData.location,
            price_new: adType === 'product' && formData.price ? parseFloat(formData.price) : null,
            price_rental: adType === 'rental' && formData.price ? parseFloat(formData.price) : 0,
            show_price: true,
            images: formData.images,
            status: 'pending',
            created_by_user_id: user.id,
            is_individual_seller: true,
            stock_rental: adType === 'rental' ? 1 : 0,
            availability_status: 'available',
          };
          await axios.post(`${API}/products`, payload);
        } else if (adType === 'request') {
          const payload = {
            title_fr: formData.title,
            title_en: formData.title,
            description_fr: formData.description,
            description_en: formData.description,
            request_type: 'service',
            category_id: formData.category_id || null,
            location: formData.location,
            budget: formData.price ? parseFloat(formData.price) : null,
            images: formData.images,
          };
          await axios.post(`${API}/requests/create?user_id=${user.id}`, payload);
        }
        
        await axios.post(`${API}/subscriptions/user/${user.id}/increment-ad?ad_type=${adType}`);
        toast.success('Annonce créée avec succès ! Elle sera visible après validation.');
      }
      
      setDialogOpen(false);
      setEditMode(false);
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'opération');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!existingAd) return;
    
    setDeleting(true);
    try {
      if (existingAd.type === 'product') {
        await axios.delete(`${API}/products/${existingAd.data.id}`);
      } else if (existingAd.type === 'request') {
        await axios.delete(`${API}/requests/delete/${existingAd.data.id}?user_id=${user.id}`);
      }
      
      // Decrement ad count
      await axios.post(`${API}/subscriptions/user/${user.id}/decrement-ad?ad_type=${existingAd.type}`);
      
      toast.success('Annonce supprimée avec succès !');
      setExistingAd(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!existingAd) return;
    
    setToggling(true);
    const currentStatus = existingAd.data.status;
    const newStatus = (currentStatus === 'active' || currentStatus === 'approved' || currentStatus === 'available') 
      ? 'inactive' 
      : 'active';
    
    try {
      if (existingAd.type === 'product') {
        await axios.patch(`${API}/products/${existingAd.data.id}/status?status=${newStatus}`);
      } else if (existingAd.type === 'request') {
        await axios.put(`${API}/requests/update/${existingAd.data.id}?user_id=${user.id}`, { status: newStatus });
      }
      
      toast.success(newStatus === 'inactive' ? 'Annonce désactivée' : 'Annonce réactivée');
      fetchData();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Erreur lors du changement de statut');
    } finally {
      setToggling(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category_id: '',
      location: '',
      price: '',
      images: [],
      contact_phone: '',
      contact_email: user?.email || '',
    });
    setAdType('product');
    setEditMode(false);
  };

  const canCreateAd = subscription?.status === 'active' && !existingAd;
  const adsUsed = subscription?.ads_count ? Object.values(subscription.ads_count).reduce((a, b) => a + b, 0) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <FaSpinner className="animate-spin text-4xl text-[#0A2540]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#0A2540] mb-2">📋 Mon Annonce</h1>
          <p className="text-gray-600">Votre pack Découverte vous permet de déposer 1 annonce</p>
        </div>

        {/* Subscription Status */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#635BFF] flex items-center justify-center">
                  <FaCheck className="text-white text-xl" />
                </div>
                <div>
                  <p className="font-bold text-lg">Pack Découverte</p>
                  <p className="text-gray-500 text-sm">{adsUsed}/1 annonce utilisée</p>
                </div>
              </div>
              
              <Button 
                onClick={() => navigate('/pricing')}
                className="bg-gradient-to-r from-[#00D4FF] to-[#FF8C5A] hover:opacity-90"
              >
                <FaArrowUp className="mr-2" /> Évoluer mon offre
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Existing Ad */}
        {existingAd ? (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  {existingAd.type === 'product' && existingAd.data.price_rental && <FaKey className="text-[#00D4FF]" />}
                  {existingAd.type === 'product' && !existingAd.data.price_rental && <FaBox className="text-[#635BFF]" />}
                  {existingAd.type === 'request' && <FaSearch className="text-[#0A2540]" />}
                  Votre annonce
                </div>
                <div className="flex gap-2 flex-wrap">
                  {/* Toggle Active/Inactive Button */}
                  {(existingAd.data.status === 'active' || existingAd.data.status === 'approved' || existingAd.data.status === 'available') ? (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleToggleStatus}
                      disabled={toggling}
                      className="text-[#00D4FF] border-orange-500 hover:bg-orange-50"
                    >
                      {toggling ? <FaSpinner className="animate-spin mr-1" /> : <FaPause className="mr-1" />}
                      Désactiver
                    </Button>
                  ) : existingAd.data.status === 'inactive' ? (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleToggleStatus}
                      disabled={toggling}
                      className="text-[#635BFF] border-[#635BFF] hover:bg-[#635BFF]/5"
                    >
                      {toggling ? <FaSpinner className="animate-spin mr-1" /> : <FaPlay className="mr-1" />}
                      Réactiver
                    </Button>
                  ) : null}
                  
                  <Button variant="outline" size="sm" onClick={openEditDialog}>
                    <FaEdit className="mr-1" /> Modifier
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="text-red-500 border-red-500 hover:bg-red-50">
                        <FaTrash className="mr-1" /> Supprimer
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer l'annonce ?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Cette action est irréversible. Votre annonce sera définitivement supprimée.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={handleDelete}
                          className="bg-red-500 hover:bg-red-600"
                          disabled={deleting}
                        >
                          {deleting ? <FaSpinner className="animate-spin mr-2" /> : null}
                          Supprimer
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-6 flex-wrap md:flex-nowrap">
                {existingAd.data.images?.[0] ? (
                  <img 
                    src={existingAd.data.images[0]} 
                    alt="Annonce"
                    className="w-32 h-32 object-cover rounded-lg"
                  />
                ) : (
                  <div className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                    <FaImage className="text-gray-400 text-2xl" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#0A2540] mb-2">
                    {existingAd.data.name_fr || existingAd.data.title_fr}
                  </h3>
                  <p className="text-gray-600 mb-3 line-clamp-2">
                    {existingAd.data.description_fr}
                  </p>
                  
                  <div className="flex items-center gap-4 flex-wrap">
                    <Badge className={
                      existingAd.data.status === 'active' || existingAd.data.status === 'approved'
                        ? 'bg-[#635BFF]' 
                        : existingAd.data.status === 'pending' 
                          ? 'bg-yellow-500' 
                          : existingAd.data.status === 'inactive'
                            ? 'bg-[#00D4FF]'
                            : existingAd.data.status === 'rejected'
                              ? 'bg-red-500'
                              : 'bg-gray-500'
                    }>
                      {existingAd.data.status === 'pending' ? 'En attente de validation' : 
                       existingAd.data.status === 'active' || existingAd.data.status === 'approved' ? 'Active' : 
                       existingAd.data.status === 'inactive' ? 'Désactivée' :
                       existingAd.data.status === 'rejected' ? 'Rejetée' :
                       existingAd.data.status}
                    </Badge>
                    
                    {existingAd.data.price_new && (
                      <span className="font-bold text-[#635BFF]">{existingAd.data.price_new} €</span>
                    )}
                    {existingAd.data.price_rental && (
                      <span className="font-bold text-[#00D4FF]">{existingAd.data.price_rental} €/jour</span>
                    )}
                    {existingAd.data.budget && (
                      <span className="font-bold text-[#0A2540]">Budget: {existingAd.data.budget} €</span>
                    )}
                    
                    {existingAd.data.location && (
                      <span className="text-gray-500 text-sm">📍 {existingAd.data.location}</span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="mt-6 pt-4 border-t flex justify-between items-center flex-wrap gap-3">
                <p className="text-sm text-gray-500">
                  Type: {existingAd.type === 'product' && existingAd.data.price_rental ? 'Matériel à louer' :
                         existingAd.type === 'product' ? 'Produit à vendre' : 
                         'Demande de service'}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => {
                    if (existingAd.type === 'product') navigate(`/product/${existingAd.data.id}`);
                    else navigate(`/requests/${existingAd.data.id}`);
                  }}>
                    <FaEye className="mr-2" /> Voir l'annonce
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="mb-6">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
                <FaPlus className="text-3xl text-gray-400" />
              </div>
              <h3 className="text-xl font-bold text-[#0A2540] mb-2">Créez votre annonce</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Vous pouvez déposer 1 annonce : vendre un produit, proposer du matériel en location, ou publier une demande de service.
              </p>
              
              <Button 
                size="lg" 
                className="bg-[#635BFF] hover:bg-[#43A047]"
                disabled={!canCreateAd}
                onClick={() => { resetForm(); setDialogOpen(true); }}
              >
                <FaPlus className="mr-2" /> Créer mon annonce
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Conversations Section */}
        {existingAd && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FaComments className="text-[#00D4FF]" />
                Messages reçus ({conversations.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {conversations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FaComments className="mx-auto text-4xl text-gray-300 mb-3" />
                  <p>Aucun message pour le moment</p>
                  <p className="text-sm">Les personnes intéressées pourront vous contacter ici</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {conversations.map((conv, idx) => (
                    <div 
                      key={conv.id || idx}
                      className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer flex justify-between items-center"
                      onClick={() => {
                        if (existingAd.type === 'request') {
                          navigate(`/requests/${existingAd.data.id}`);
                        } else {
                          navigate(`/supplier/quote-thread/${conv.id}`);
                        }
                      }}
                    >
                      <div>
                        <p className="font-medium">{conv.client_name || conv.requester_name || 'Utilisateur'}</p>
                        <p className="text-sm text-gray-500">
                          {conv.last_message || conv.message || 'Cliquez pour voir la conversation'}
                        </p>
                      </div>
                      <Badge variant="outline">Voir</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Upgrade CTA */}
        <Card className="bg-gradient-to-r from-[#0A2540] to-[#006D8F] text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                  <FaCrown className="text-yellow-400" /> Besoin de plus ?
                </h3>
                <p className="text-white/80">
                  Passez au pack Fournisseur pour 20 annonces, une boutique complète et plus encore !
                </p>
              </div>
              <Button 
                onClick={() => navigate('/pricing')}
                className="bg-white text-[#0A2540] hover:bg-gray-100"
              >
                Voir les offres
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Modifier l\'annonce' : 'Créer une annonce'}</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {!editMode && (
                <div>
                  <Label className="mb-3 block">Type d'annonce</Label>
                  <Tabs value={adType} onValueChange={setAdType}>
                    <TabsList className="grid grid-cols-3 w-full">
                      <TabsTrigger value="product" className="flex items-center gap-2">
                        <FaBox /> Vente
                      </TabsTrigger>
                      <TabsTrigger value="rental" className="flex items-center gap-2">
                        <FaKey /> Location
                      </TabsTrigger>
                      <TabsTrigger value="request" className="flex items-center gap-2">
                        <FaSearch /> Demande
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              )}
              
              <div>
                <Label htmlFor="title">
                  {adType === 'request' ? 'Titre de votre demande *' : 'Titre de votre annonce *'}
                </Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder={
                    adType === 'product' ? 'Ex: Tractopelle JCB 3CX - Bon état' :
                    adType === 'rental' ? 'Ex: Groupe électrogène 50 kVA' :
                    'Ex: Recherche étude de sol pour construction'
                  }
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Décrivez votre annonce en détail..."
                  rows={4}
                />
              </div>
              
              <div>
                <Label>Catégorie</Label>
                <Select value={formData.category_id} onValueChange={(v) => setFormData({...formData, category_id: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name_fr || cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="location">Localisation</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder="Ville, Pays"
                  />
                </div>
                <div>
                  <Label htmlFor="price">
                    {adType === 'product' ? 'Prix (€)' : 
                     adType === 'rental' ? 'Prix/jour (€)' : 
                     'Budget (€)'}
                  </Label>
                  <Input
                    id="price"
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
              </div>
              
              <div>
                <Label>Photos (max 5)</Label>
                <div className="mt-2">
                  <div className="flex flex-wrap gap-3 mb-3">
                    {formData.images.map((img, idx) => (
                      <div key={idx} className="relative w-20 h-20">
                        <img src={img} alt="" className="w-full h-full object-cover rounded" />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {formData.images.length < 5 && (
                      <label className="w-20 h-20 border-2 border-dashed border-gray-300 rounded flex items-center justify-center cursor-pointer hover:bg-gray-50">
                        {uploadingImage ? (
                          <FaSpinner className="animate-spin text-gray-400" />
                        ) : (
                          <FaImage className="text-gray-400" />
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          className="hidden"
                          disabled={uploadingImage}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Annuler
                </Button>
                <Button type="submit" disabled={submitting} className="bg-[#635BFF]">
                  {submitting ? <FaSpinner className="animate-spin mr-2" /> : <FaCheck className="mr-2" />}
                  {editMode ? 'Enregistrer' : 'Publier l\'annonce'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
